import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import {
  DownloadIcon,
  RightArrowIcon,
  StarIcon,
  YaySmallIcon,
} from 'secondstep-components'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { PLAYBOOK_STEP_MAP } from 'utils/adminExperienceHelpers'
import TierLabel from 'components/TierLabel'
import NavigatorBubbleButton from './NavigatorBubbleButton'
import NavigatorButton from './NavigatorButton'
import {
  BubbleNavigatorWrapper,
  DateText,
  EditButton,
  EstimatedTime,
  GetStartedButton,
  GoalContentWrapper,
  GoalContentHeaderWrapper,
  GoalCompletedText,
  GoalStatusBadge,
  GoalSubTitle,
  GoalText,
  GoalTextLarge,
  GoalTitleWrapper,
  Image,
  PageContent,
  PageNavigator,
  PlaybookStepsWrapper,
  RightContent,
  StepNumber,
  StyledDateRange,
  StyledEditIcon,
  StyledGoalHr,
  StyledHeaderTwo,
  StyledHr,
  StyledLinkDropdown,
  StyledProgressBar,
  SubTitle,
  SummaryButton,
  SummaryLink,
  TextContent,
  TierPill,
  Title,
  TopWrapper,
} from './component.styles'
import {
  ALL_STAFF_BUTTON_TEXT,
  BTS_2026_FLAG,
  DATE_TEST_FLAG,
  EDIT_BUTTON_TEST_ID,
  FAMILIES_BUTTON_TEXT,
  GET_STARTED_BUTTON_TEXT,
  GET_STARTED_BUTTON_TEST_ID,
  GOALS_TEXT,
  HEADER_TEXT_DEFAULT,
  HEADER_TEXT_SUMMARY,
  PAGE_CONTENT_TEST_ID,
  PURPOSE_TEXT,
  RESUME_BUTTON_TEXT,
  SUMMARY_LINK_TEXT,
} from './constants'
import { goalNavigationData, modalMap, stepDataMap } from './data'
import { isFlagOn } from 'utils/featureFlags'

/** Maps ?modalId= query (1–4) to `currentPlanStep` used by handleModalClose / stepDataMap. */
const planStepIndexFromModalQueryParam = modalId => {
  const n = Number(modalId)
  if (Number.isNaN(n) || n < 1 || n > 4) return null
  if (n === 4) return 3
  return n - 1
}

const PlaybookSteps = ({
  classCreationGoalValue,
  error,
  familyLetterSent,
  fetchK8PlaybookData,
  goal1Date,
  goal1IsCompleted,
  goal2Date,
  goal2IsCompleted,
  goal3Date,
  goal3IsCompleted,
  goal3Text,
  goal4Date,
  goal4IsCompleted,
  goal4Text,
  goal5Date,
  goal5IsCompleted,
  goal5Text,
  goalsAreCompleted,
  goalsIsInDraft,
  hasAllStepsCompleted,
  implementationApproachIsCompleted,
  isFetching,
  isSingleSite,
  kickoffHosted,
  kickoffPrepared,
  lessonCompletionGoalIsDisabled,
  lessonCompletionGoalValue,
  linkDropdownDisplayTitle,
  linkMenu,
  navigationEvent,
  panelData,
  playbookData,
  programStartDate,
  purposeIsCompleted,
  purposeIsInDraft,
  showHeader,
  showMonitorViewOnly,
  siteId,
  userEmail,
  userInvitationMethod,
}) => {
  const [activePanel, setActivePanel] = useState(null)
  const [activeStep, setActiveStep] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [ModalComponent, setModalComponent] = useState(null)
  const [selectedGoal, setSelectedGoal] = useState('goal 1')
  const [showSummary, setShowSummary] = useState(false)
  const [sitePlaybookData, setSitePlaybookData] = useState({})
  const [currentPlanStep, setCurrentPlanStep] = useState(0)

  const location = useLocation()
  const handleModalIdRef = useRef(null)
  const modalId = useMemo(() => {
    const searchParams = new URLSearchParams(location?.search || '')
    return searchParams.get('modalId')
  }, [location?.search])

  useEffect(() => {
    if (showMonitorViewOnly && isSingleSite) {
      if (!modalId) {
        handleModalIdRef.current = null
        return
      }

      if (handleModalIdRef.current === modalId) return

      handleModalIdRef.current = modalId
      const stepFromQuery = planStepIndexFromModalQueryParam(modalId)
      if (stepFromQuery !== null) {
        setCurrentPlanStep(stepFromQuery)
      }
      launchModal(modalId)
    }
  }, [modalId])

  const dropdownLinkNavigation = link => {
    const { download = null, openSameTab, url: optionUrlPath } = link || {}

    if (download) {
      navigationEvent(
        download?.file?.file?.url,
        NavigationHelper.types.DOWNLOAD_LINK,
      )
    }

    const type = openSameTab
      ? NavigationHelper.types.PUSH
      : NavigationHelper.types.NEW_TAB
    navigationEvent(optionUrlPath, type)
  }

  const handleActiveStep = () => {
    const stepConditions = [
      () => !implementationApproachIsCompleted,
      () => !goalsAreCompleted,
      () => !purposeIsCompleted,
    ]

    const newStep = stepConditions.findIndex(condition => condition())
    setActiveStep(newStep > 0 ? newStep : 0)
  }

  const handleGoalNavigation = goalKey => {
    setSelectedGoal(goalKey)
  }

  const isGoalDefined = goalKey => {
    const optionalGoalIsDefinedProps = {
      goal3IsCompleted,
      goal4IsCompleted,
      goal5IsCompleted,
    }
    const goalData = goalNavigationData.find(goal => goal.key === goalKey)
    if (!goalData) return false

    if (!goalData.isOptional) return true

    return goalData.isCompletedProp
      ? optionalGoalIsDefinedProps[goalData.isCompletedProp]
      : true
  }

  const launchModal = modalName => {
    const Modal = modalMap[modalName]?.component
    if (Modal) {
      setModalComponent(() => Modal)
      setIsModalOpen(true)
    }
  }

  const handleModalClose = () => {
    const nextStepIndex = currentPlanStep + 1
    if (nextStepIndex < Object.keys(stepDataMap).length) {
      setCurrentPlanStep(nextStepIndex)
      const nextStep = stepDataMap[nextStepIndex]
      launchModal(nextStep.modalName)
      return
    }

    setIsModalOpen(false)
    setModalComponent(null)
  }

  const setButtonText = modalName => {
    let buttonText = GET_STARTED_BUTTON_TEXT

    if (modalName === GOALS_TEXT && goalsIsInDraft) {
      buttonText = RESUME_BUTTON_TEXT
    }
    if (modalName === PURPOSE_TEXT && purposeIsInDraft) {
      buttonText = RESUME_BUTTON_TEXT
    }

    return buttonText
  }

  const renderGoal = (goalDate, goalText) => {
    const goalCompletedMap = {
      'goal 1': goal1IsCompleted,
      'goal 2': goal2IsCompleted,
      'goal 3': goal3IsCompleted,
      'goal 4': goal4IsCompleted,
      'goal 5': goal5IsCompleted,
    }
    const selectedGoalIsCompleted = goalCompletedMap[selectedGoal] || false
    const isGoal1 = selectedGoal === 'goal 1'
    const isGoal2 = selectedGoal === 'goal 2'
    const isRequiredGoal = isGoal1 || selectedGoal === 'goal 2'
    const endDateInMs = new Date(goalDate).getTime()
    const endDate = new Date(goalDate)
    const endDateTextFormatted = endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    const programStartDateInMs = new Date(programStartDate).getTime()
    const startDateTextFormatted = new Date(
      programStartDateInMs,
    ).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    const classesCreatedPercentage = Math.round(
      (sitePlaybookData?.classes_created / classCreationGoalValue) * 100,
    )
    const renderGoalStatusText = () => {
      let goalStatusText = null
      const todaysDate = new Date()
      todaysDate.setHours(8, 0, 0, 0)
      const todaysDateTimeStamp = todaysDate.toISOString().slice(0, 19)

      if (isGoal1) {
        if (todaysDateTimeStamp >= goal1Date) {
          if (sitePlaybookData?.classes_created < classCreationGoalValue) {
            goalStatusText = 'Goal Missed'
          }
          if (sitePlaybookData?.classes_created === classCreationGoalValue) {
            goalStatusText = 'Goal Met'
          }
          if (sitePlaybookData?.classes_created > classCreationGoalValue) {
            goalStatusText = 'Goal Exceeded'
          }
        }
      }
      if (isGoal2) {
        if (todaysDateTimeStamp >= goal2Date) {
          if (
            sitePlaybookData?.schoolwide_lesson_completion_percentage <
            lessonCompletionGoalValue
          ) {
            goalStatusText = 'Goal Missed'
          }
          if (
            Math.floor(
              sitePlaybookData?.schoolwide_lesson_completion_percentage * 100,
            ) === lessonCompletionGoalValue
          ) {
            goalStatusText = 'Goal Met'
          }
          if (
            sitePlaybookData?.schoolwide_lesson_completion_percentage >
            lessonCompletionGoalValue
          ) {
            goalStatusText = 'Goal Exceeded'
          }
        }
      }
      return goalStatusText
    }

    const goalStatusText = renderGoalStatusText()
    const classesCreated = sitePlaybookData?.classes_created || 0
    const lessonsMarkedDone = sitePlaybookData?.lessons_marked_done || 0
    const lessonCompletionPercentage = Math.floor(
      sitePlaybookData?.schoolwide_lesson_completion_percentage * 100,
    )

    return (
      <Fragment key="goal-content-completed">
        {!selectedGoalIsCompleted && (
          <GoalContentWrapper
            isSingleSite={isSingleSite}
            selectedGoalIsCompleted={selectedGoalIsCompleted}
            showHeader={showHeader}
            showMonitorViewOnly={showMonitorViewOnly}
          >
            <GoalTextLarge selectedGoalIsCompleted={selectedGoalIsCompleted}>
              {`${isGoal1 ? classesCreated : lessonCompletionPercentage}${
                isGoal2 ? '%' : ''
              }`}
            </GoalTextLarge>
            <GoalText selectedGoalIsCompleted={selectedGoalIsCompleted}>
              {isGoal1 ? 'Classes Created' : 'Core Lessons Complete'}
            </GoalText>
          </GoalContentWrapper>
        )}
        {selectedGoalIsCompleted && (
          <GoalContentWrapper
            isSingleSite={isSingleSite}
            selectedGoalIsCompleted={selectedGoalIsCompleted}
            showHeader={showHeader}
            showMonitorViewOnly={showMonitorViewOnly}
          >
            {goalStatusText !== null &&
              (selectedGoal === 'goal 1' || selectedGoal === 'goal 2') && (
                <GoalStatusBadge goalStatusText={goalStatusText}>
                  {goalStatusText !== 'Goal Missed' && (
                    <StarIcon id="goal-status-icon" />
                  )}
                  <span>{goalStatusText}</span>
                </GoalStatusBadge>
              )}
            <GoalContentHeaderWrapper>
              {(isGoal1 || (!isGoal1 && !programStartDate)) && (
                <DateText>Target Date: {endDateTextFormatted}</DateText>
              )}
              {!isGoal1 && programStartDate && (
                <StyledDateRange
                  endDateInMs={endDateInMs}
                  endDateTextFormatted={endDateTextFormatted}
                  progressBarView={true}
                  startDateInMs={programStartDateInMs}
                  startDateTextFormatted={startDateTextFormatted}
                />
              )}
              {isFlagOn([BTS_2026_FLAG]) && (
                <EditButton
                  data-testid={EDIT_BUTTON_TEST_ID}
                  onClick={() => launchModal(GOALS_TEXT)}
                >
                  <StyledEditIcon aria-label="Edit Goals" />
                </EditButton>
              )}
            </GoalContentHeaderWrapper>
            <StyledGoalHr showMonitorViewOnly={showMonitorViewOnly} />
            {isRequiredGoal && (
              <Fragment key="goal-content-completed">
                <GoalTitleWrapper>
                  <GoalSubTitle>
                    {isGoal1 ? 'Classes Created' : 'Core Lessons Complete'}
                  </GoalSubTitle>
                  <GoalCompletedText>
                    {isGoal1 ? classesCreated : lessonsMarkedDone} of{' '}
                    {isGoal1
                      ? classCreationGoalValue
                      : sitePlaybookData?.total_lessons}{' '}
                    {isGoal1 ? 'classes' : 'lessons'}
                  </GoalCompletedText>
                </GoalTitleWrapper>
                <StyledProgressBar
                  goalPercentage={
                    !isGoal1 ? lessonCompletionGoalValue : undefined
                  }
                  goalValue={isGoal1 ? classCreationGoalValue : undefined}
                  hideProgressText={true}
                  percentComplete={
                    isGoal1
                      ? classesCreatedPercentage
                      : lessonCompletionPercentage
                  }
                  showGoalIndicator={true}
                />
              </Fragment>
            )}
            {!isRequiredGoal && (
              <Fragment key="custom-goal-content">
                <GoalSubTitle>Custom Goal</GoalSubTitle>
                <GoalText selectedGoalIsCompleted={selectedGoalIsCompleted}>
                  {goalText}
                </GoalText>
              </Fragment>
            )}
          </GoalContentWrapper>
        )}
      </Fragment>
    )
  }

  const renderGoalContent = () => {
    if (selectedGoal === 'goal 1') {
      return renderGoal(goal1Date)
    }
    if (selectedGoal === 'goal 2') {
      return renderGoal(goal2Date)
    }
    if (selectedGoal === 'goal 3') {
      return renderGoal(goal3Date, goal3Text)
    }
    if (selectedGoal === 'goal 4') {
      return renderGoal(goal4Date, goal4Text)
    }
    if (selectedGoal === 'goal 5') {
      return renderGoal(goal5Date, goal5Text)
    }
  }

  useEffect(() => {
    if (!error && !isFetching && !playbookData) {
      fetchK8PlaybookData(userEmail, siteId)
    }
  }, [error, isFetching, playbookData])

  useEffect(() => {
    if (panelData?.length > 0) {
      const panel = panelData.find(({ header }) =>
        header.toLowerCase().includes('plan'),
      )
      setActivePanel(panel)
    }
  }, [panelData, selectedGoal])

  useEffect(() => {
    const playbookDataBySiteId =
      playbookData?.find(site => site.site_id === siteId) ||
      playbookData?.[0] ||
      {}
    setSitePlaybookData(playbookDataBySiteId)
  }, [playbookData, siteId])

  useEffect(() => {
    if (
      goalsAreCompleted &&
      implementationApproachIsCompleted &&
      purposeIsCompleted
    ) {
      setShowSummary(true)
      setActiveStep(3)
    } else {
      setShowSummary(false)
      handleActiveStep()
    }
  }, [
    activeStep,
    familyLetterSent,
    goalsAreCompleted,
    implementationApproachIsCompleted,
    kickoffHosted,
    kickoffPrepared,
    purposeIsCompleted,
    showSummary,
    userInvitationMethod,
  ])

  useEffect(() => {
    if (showMonitorViewOnly) {
      setSelectedGoal(goalNavigationData[0].key)
    }
  }, [showMonitorViewOnly])

  useEffect(() => {
    // Reset to Goal 1 if currently selected optional goal has been removed
    if (selectedGoal && !isGoalDefined(selectedGoal)) {
      setSelectedGoal(goalNavigationData[0].key)
    }
  }, [selectedGoal, goal3IsCompleted, goal4IsCompleted, goal5IsCompleted])

  return (
    <PlaybookStepsWrapper
      isSingleSite={isSingleSite}
      showHeader={showHeader}
      showMonitorViewOnly={showMonitorViewOnly}
    >
      {showHeader && (
        <TopWrapper showMonitorViewOnly={showMonitorViewOnly}>
          {!showMonitorViewOnly && (
            <StyledHeaderTwo>
              {showSummary ? HEADER_TEXT_SUMMARY : HEADER_TEXT_DEFAULT}
            </StyledHeaderTwo>
          )}
          {showMonitorViewOnly && (
            <StyledHeaderTwo>School Progress</StyledHeaderTwo>
          )}
          {!showMonitorViewOnly && hasAllStepsCompleted && (
            <SummaryLink
              onClick={() => {
                setCurrentPlanStep(3)
                launchModal(SUMMARY_LINK_TEXT)
              }}
            >
              {SUMMARY_LINK_TEXT}
            </SummaryLink>
          )}
          {showMonitorViewOnly && (
            <TierPill>
              <TierLabel
                labelText={
                  isFlagOn([DATE_TEST_FLAG])
                    ? PLAYBOOK_STEP_MAP.tierLabelTest
                    : PLAYBOOK_STEP_MAP.tierLabel
                }
              />
            </TierPill>
          )}
        </TopWrapper>
      )}
      {showMonitorViewOnly && (
        <BubbleNavigatorWrapper>
          {goalNavigationData.map(goal => {
            if (!isGoalDefined(goal.key)) {
              return null
            }
            if (goal.key === 'goal 2' && lessonCompletionGoalIsDisabled) {
              return null
            }

            return (
              <NavigatorBubbleButton
                dataTestId={`goal-button-${goal.key}`}
                isActive={selectedGoal === goal.key}
                key={goal.key}
                onClick={() => handleGoalNavigation(goal.key)}
                showHeader={showHeader}
                showMonitorViewOnly={showMonitorViewOnly}
              >
                {goal.label}
              </NavigatorBubbleButton>
            )
          })}
        </BubbleNavigatorWrapper>
      )}
      {showMonitorViewOnly && renderGoalContent()}
      {!showMonitorViewOnly && (
        <PageContent
          dataTestId={PAGE_CONTENT_TEST_ID}
          showSummary={showSummary}
        >
          {!showSummary && !showMonitorViewOnly && (
            <Fragment key="not-complete-step">
              {activePanel?.modalFlows[activeStep]?.image?.file?.url && (
                <Image
                  alt={activePanel?.modalFlows[activeStep]?.image?.title}
                  src={activePanel?.modalFlows[activeStep]?.image?.file?.url}
                />
              )}
              <TextContent>
                <StepNumber>
                  {activePanel?.modalFlows[activeStep]?.subhead}
                </StepNumber>
                <Title>
                  {activePanel?.modalFlows[activeStep]?.shortHeader}
                </Title>
                <EstimatedTime>
                  {activePanel?.modalFlows[activeStep]?.duration}
                </EstimatedTime>
              </TextContent>
              <GetStartedButton
                dataTestId={GET_STARTED_BUTTON_TEST_ID}
                onClick={() => {
                  setCurrentPlanStep(activeStep)
                  launchModal(stepDataMap?.[activeStep]?.modalName)
                }}
              >
                {setButtonText(stepDataMap?.[activeStep]?.modalName)}
              </GetStartedButton>
            </Fragment>
          )}
          {showSummary && !showMonitorViewOnly && (
            <Fragment key="complete-step">
              <YaySmallIcon id="step-icon" />
              <TextContent>
                <Title>Ready to launch?</Title>
                <SubTitle>
                  Use these templates to help your school launch with
                  confidence.
                </SubTitle>
              </TextContent>
              <RightContent>
                {linkMenu && (
                  <StyledLinkDropdown
                    className="download-action-plan-button"
                    defaultLabel={linkDropdownDisplayTitle}
                    dropdownButtonIcon={<DownloadIcon />}
                    key="link-dropdown"
                    keyProp="id"
                    labelProp="buttonText"
                    onChange={link => dropdownLinkNavigation(link)}
                    options={linkMenu}
                    shouldHideSelectedIcon={true}
                  />
                )}
                <SummaryButton
                  onClick={() => {
                    setCurrentPlanStep(3)
                    launchModal(ALL_STAFF_BUTTON_TEXT)
                  }}
                >
                  {ALL_STAFF_BUTTON_TEXT}
                  <RightArrowIcon />
                </SummaryButton>
                <SummaryButton
                  onClick={() => {
                    setCurrentPlanStep(3)
                    launchModal(FAMILIES_BUTTON_TEXT)
                  }}
                >
                  {FAMILIES_BUTTON_TEXT}
                  <RightArrowIcon />
                </SummaryButton>
              </RightContent>
            </Fragment>
          )}
        </PageContent>
      )}
      {!showMonitorViewOnly && !showSummary && (
        <PageNavigator>
          <NavigatorButton
            isActive={activeStep === 0}
            isCompleted={implementationApproachIsCompleted}
            label={stepDataMap[0].label}
            onClick={() => {
              setCurrentPlanStep(0)
              launchModal(stepDataMap[0].modalName)
            }}
          >
            {stepDataMap[0].label}
          </NavigatorButton>
          <StyledHr />
          <NavigatorButton
            isActive={activeStep === 1}
            isCompleted={goalsAreCompleted}
            label={stepDataMap[1].label}
            onClick={() => {
              setCurrentPlanStep(1)
              launchModal(stepDataMap[1].modalName)
            }}
          >
            {stepDataMap[1].label}
          </NavigatorButton>
          <StyledHr />
          <NavigatorButton
            isActive={activeStep === 2}
            isCompleted={purposeIsCompleted}
            label={stepDataMap[2].label}
            onClick={() => {
              setCurrentPlanStep(2)
              launchModal(stepDataMap[2].modalName)
            }}
          >
            {stepDataMap[2].label}
          </NavigatorButton>
        </PageNavigator>
      )}
      {isModalOpen && ModalComponent && (
        <ModalComponent
          handleCancel={() => {
            setIsModalOpen(false)
          }}
          handleClose={handleModalClose}
          siteId={siteId}
        />
      )}
    </PlaybookStepsWrapper>
  )
}

PlaybookSteps.propTypes = {
  adminSetK8PlaybookStepsDropdown: PropTypes.func,
  classCreationGoalValue: PropTypes.number,
  error: PropTypes.object,
  familyLetterSent: PropTypes.bool,
  fetchK8PlaybookData: PropTypes.func,
  goal1Date: PropTypes.number,
  goal1IsCompleted: PropTypes.bool,
  goal2Date: PropTypes.number,
  goal2IsCompleted: PropTypes.bool,
  goal3Date: PropTypes.number,
  goal3IsCompleted: PropTypes.bool,
  goal3Text: PropTypes.string,
  goal4Date: PropTypes.number,
  goal4IsCompleted: PropTypes.bool,
  goal4Text: PropTypes.string,
  goal5Date: PropTypes.number,
  goal5IsCompleted: PropTypes.bool,
  goal5Text: PropTypes.string,
  goalsAreCompleted: PropTypes.bool,
  goalsIsInDraft: PropTypes.bool,
  hasAllStepsCompleted: PropTypes.bool,
  implementationApproachIsCompleted: PropTypes.bool,
  isFetching: PropTypes.bool,
  isSingleSite: PropTypes.bool,
  kickoffHosted: PropTypes.bool,
  kickoffPrepared: PropTypes.bool,
  lessonCompletionGoalIsDisabled: PropTypes.bool,
  lessonCompletionGoalValue: PropTypes.number,
  linkDropdownDisplayTitle: PropTypes.string,
  linkMenu: PropTypes.array,
  navigationEvent: PropTypes.func,
  panelData: PropTypes.array,
  playbookData: PropTypes.array,
  programStartDate: PropTypes.number,
  purposeIsCompleted: PropTypes.bool,
  purposeIsInDraft: PropTypes.bool,
  showHeader: PropTypes.bool,
  showMonitorViewOnly: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userEmail: PropTypes.string,
  userInvitationMethod: PropTypes.string,
}

export default PlaybookSteps
